import { type CueLayout } from '~/components/AssetModalV2/Content/Panel/Captions/captionStyleTypes';
import { type CaptionCueLayout } from '~/components/AssetModalV2/Content/Panel/Captions/getCaptionCueLayout';
import { type TranscriptSegmentType } from '~/components/AssetModalV2/Content/Panel/Transcript/types';

export type CaptionTimedWord = {
  endTimeMs: number;
  startTimeMs: number;
  text: string;
};

export type BuiltCaptionCue = {
  endTimeMs: number;
  lines: CaptionTimedWord[][];
  startTimeMs: number;
};

/**
 * Merges a segment's items into ordered words: punctuation attaches to the
 * preceding word; leading punctuation stands alone. Empty items are dropped.
 * Mirrors backend `cueBuilder.wordsFromSegment`.
 */
export const wordsFromCaptionSegment = (segment: TranscriptSegmentType): CaptionTimedWord[] => {
  const words: CaptionTimedWord[] = [];

  for (const item of segment.items) {
    const text = item.text.trim();

    if (!text) {
      continue;
    }

    const last = words[words.length - 1];

    if (item.type === 'punctuation' && last) {
      last.text += text;
      last.endTimeMs = Math.max(last.endTimeMs, item.endTimeMs);
      continue;
    }

    words.push({
      text,
      startTimeMs: item.startTimeMs,
      endTimeMs: item.endTimeMs,
    });
  }

  return words;
};

/** Splits ordered words on cue-size, cue-duration, and pause boundaries. */
const chunkWords = (words: CaptionTimedWord[], layout: CueLayout): CaptionTimedWord[][] => {
  const chunks: CaptionTimedWord[][] = [];

  words.forEach((word) => {
    const current = chunks[chunks.length - 1];
    const shouldBreak =
      !current ||
      current.length >= layout.maxWordsPerCue ||
      word.endTimeMs - current[0].startTimeMs > layout.maxCueDurationMs ||
      word.startTimeMs - current[current.length - 1].endTimeMs >= layout.pauseBreakMs;

    if (shouldBreak) {
      chunks.push([word]);
    } else {
      current.push(word);
    }
  });

  return chunks;
};

/** Greedily wraps words into lines of at most `maxCharsPerLine` characters. */
const wrapWordsIntoLines = (words: CaptionTimedWord[], maxCharsPerLine: number): CaptionTimedWord[][] => {
  const lines: CaptionTimedWord[][] = [];

  words.forEach((word) => {
    const current = lines[lines.length - 1];
    const currentLength = current ? current.reduce((total, entry) => total + entry.text.length + 1, -1) : 0;

    if (!current || currentLength + 1 + word.text.length > maxCharsPerLine) {
      lines.push([word]);
    } else {
      current.push(word);
    }
  });

  return lines;
};

const cueFromLines = (lines: CaptionTimedWord[][]): BuiltCaptionCue => {
  const flatWords = lines.flat();
  const startTimeMs = flatWords[0]?.startTimeMs ?? 0;
  const endTimeMs = Math.max(...flatWords.map((word) => word.endTimeMs), startTimeMs + 1);

  return { startTimeMs, endTimeMs, lines };
};

/**
 * Builds display cues from transcript segments. Cues never cross segment
 * boundaries; overflowing lines start a new cue; cue times are monotonic and
 * clamped so consecutive cues never overlap.
 *
 * `layout.maxCharsPerLine` must already be aspect-scaled when the caller
 * applies burn parity in the overlay (see `scaleCaptionMaxCharsPerLine`).
 */
export const buildCaptionCues = ({
  layout,
  segments,
}: {
  layout: CueLayout;
  segments: TranscriptSegmentType[];
}): BuiltCaptionCue[] => {
  const cues = segments
    .flatMap((segment) => chunkWords(wordsFromCaptionSegment(segment), layout))
    .flatMap((chunk) => {
      const lines = wrapWordsIntoLines(chunk, layout.maxCharsPerLine);
      const cueLineGroups: CaptionTimedWord[][][] = [];

      lines.forEach((line) => {
        const current = cueLineGroups[cueLineGroups.length - 1];

        if (!current || current.length >= layout.maxLinesPerCue) {
          cueLineGroups.push([line]);
        } else {
          current.push(line);
        }
      });

      return cueLineGroups.map(cueFromLines);
    });

  return cues.map((cue, index) => {
    const next = cues[index + 1];

    return next && cue.endTimeMs > next.startTimeMs
      ? { ...cue, endTimeMs: Math.max(next.startTimeMs, cue.startTimeMs + 1) }
      : cue;
  });
};

export const getCaptionWordsFromSegments = (segments: TranscriptSegmentType[]): CaptionTimedWord[] =>
  segments.flatMap(wordsFromCaptionSegment);

export const toCaptionCueLayout = ({
  cue,
  words,
}: {
  cue: BuiltCaptionCue;
  words: CaptionTimedWord[];
}): CaptionCueLayout => {
  const wordIndex = (word: CaptionTimedWord) =>
    words.findIndex(
      (candidate) =>
        candidate.startTimeMs === word.startTimeMs &&
        candidate.endTimeMs === word.endTimeMs &&
        candidate.text === word.text,
    );

  const flatWords = cue.lines.flat();
  const startIndex = wordIndex(flatWords[0] ?? { text: '', startTimeMs: 0, endTimeMs: 0 });
  const lastWord = flatWords[flatWords.length - 1];
  const endIndex = lastWord ? wordIndex(lastWord) + 1 : startIndex;

  return {
    endIndex: Math.max(startIndex + 1, endIndex),
    lines: cue.lines.map((line) => {
      const lineStartIndex = wordIndex(line[0] ?? { text: '', startTimeMs: 0, endTimeMs: 0 });
      const lineLastWord = line[line.length - 1];
      const lineEndIndex = lineLastWord ? wordIndex(lineLastWord) + 1 : lineStartIndex;

      return {
        endIndex: Math.max(lineStartIndex + 1, lineEndIndex),
        startIndex: lineStartIndex,
      };
    }),
    startIndex: Math.max(0, startIndex),
  };
};
